import React, { useState, useEffect } from 'react';
import { useStore } from '@/store/useStore';
import api from '@/lib/api';
import { 
  Target,
  TrendingUp,
  TrendingDown,
  Award,
  AlertTriangle,
  Loader2,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const FIELDS = ['All Fields', 'Spraberry', 'Wolfcamp', 'Bone Spring', 'Delaware Basin', 'Midland Basin'];

const Benchmarking = () => {
  const { counties } = useStore();
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCounty, setSelectedCounty] = useState('all');
  const [selectedField, setSelectedField] = useState('All Fields');

  useEffect(() => {
    const fetchBenchmarks = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedCounty !== 'all') params.append('county', selectedCounty);
        if (selectedField !== 'All Fields') params.append('field', selectedField);
        
        const res = await api.get(`/benchmark/wells?${params.toString()}`);
        setBenchmarkData(res.data);
      } catch (error) {
        console.error('Error fetching benchmarks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBenchmarks();
  }, [selectedCounty, selectedField]);

  const typeCurveData = benchmarkData?.type_curves?.months.map((month, idx) => ({
    month: `M${month}`,
    p10: benchmarkData.type_curves.p10[idx],
    p50: benchmarkData.type_curves.p50[idx],
    p90: benchmarkData.type_curves.p90[idx],
  })) || [];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Benchmarking
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            P10/P50/P90 type curves
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCounty} onValueChange={setSelectedCounty}>
            <SelectTrigger 
              data-testid="benchmark-county-select"
              className="w-28 sm:w-40 bg-surface border-white/10 h-8 text-xs"
            >
              <SelectValue placeholder="County" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10">
              <SelectItem value="all" className="text-xs">All Counties</SelectItem>
              {counties?.map(c => (
                <SelectItem key={c.name} value={c.name} className="text-xs">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedField} onValueChange={setSelectedField}>
            <SelectTrigger 
              data-testid="benchmark-field-select"
              className="w-28 sm:w-40 bg-surface border-white/10 h-8 text-xs"
            >
              <SelectValue placeholder="Field" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10">
              {FIELDS.map(f => (
                <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Target className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Wells</span>
            </div>
            <p className="text-xl sm:text-2xl font-heading font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : benchmarkData?.well_count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Award className="w-3.5 h-3.5 text-success" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Outperform</span>
            </div>
            <p className="text-xl sm:text-2xl font-heading font-bold text-success">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : benchmarkData?.outperformers?.length || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <AlertTriangle className="w-3.5 h-3.5 text-danger" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Underperf</span>
            </div>
            <p className="text-xl sm:text-2xl font-heading font-bold text-danger">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : benchmarkData?.underperformers?.length || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Filter className="w-3.5 h-3.5 text-secondary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Filter</span>
            </div>
            <p className="text-xs sm:text-sm font-mono text-foreground truncate">
              {selectedCounty === 'all' ? 'All' : selectedCounty}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Type Curves Chart */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
              Type Curves (P10/P50/P90)
            </CardTitle>
            <Badge variant="outline" className="border-white/20 text-[10px] sm:text-xs font-mono">
              24 MO
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {loading ? (
            <div className="h-[250px] sm:h-[400px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="h-[250px] sm:h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={typeCurveData}>
                  <defs>
                    <linearGradient id="p10Grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="p50Grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F0F10', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontFamily: 'IBM Plex Mono',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [formatNumber(value), `${name.toUpperCase()} (BBL)`]}
                  />
                  <Legend 
                    wrapperStyle={{ fontFamily: 'IBM Plex Mono', fontSize: '12px' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="p10" 
                    name="P10 (Top 10%)"
                    stroke="#10B981" 
                    fill="url(#p10Grad)"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="p50" 
                    name="P50 (Median)"
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="p90" 
                    name="P90 (Bottom 10%)"
                    stroke="#EF4444" 
                    fill="transparent"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
        {/* Outperformers */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
              <Award className="w-4 h-4 text-success" />
              Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground uppercase">API</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase">Operator</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase">County</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase text-right">Index</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benchmarkData?.outperformers?.slice(0, 8).map((well, idx) => (
                    <TableRow key={idx} className="border-white/5">
                      <TableCell className="font-mono text-xs">{well.api_number}</TableCell>
                      <TableCell className="text-sm truncate max-w-[120px]">{well.operator_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{well.county}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="border-success/50 text-success font-mono text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          +{well.performance_index.toFixed(0)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Underperformers */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-danger" />
              Underperformers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground uppercase">API</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase">Operator</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase">County</TableHead>
                    <TableHead className="text-xs text-muted-foreground uppercase text-right">Index</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benchmarkData?.underperformers?.slice(0, 8).map((well, idx) => (
                    <TableRow key={idx} className="border-white/5">
                      <TableCell className="font-mono text-xs">{well.api_number}</TableCell>
                      <TableCell className="text-sm truncate max-w-[120px]">{well.operator_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{well.county}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="border-danger/50 text-danger font-mono text-xs">
                          <TrendingDown className="w-3 h-3 mr-1" />
                          {well.performance_index.toFixed(0)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Benchmarking;
